// src/stock/InventaireDetail.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Table, Row, Col, Spinner, Button,
    Modal, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import { formatDate, formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function InventaireDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [inventaire, setInventaire] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showValidateModal, setShowValidateModal] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchInventaire();
    }, [id]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchInventaire = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/inventaires.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setInventaire(result.data);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleValider = async () => {
        setIsValidating(true);
        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;

            const response = await fetch(`${API_BASE_URL}/inventaires.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'valider',
                    id: id,
                    id_utilisateur: user?.id || user?.id_utilisateur || 1
                })
            });

            const result = await response.json();

            if (result.success) {
                setShowValidateModal(false);
                showToast('success', 'Inventaire validé', 'Les écarts ont été appliqués au stock');
                fetchInventaire();
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la validation");
            }
        } catch (err) {
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setIsValidating(false);
        }
    };

    const handleAnnuler = async () => {
        setIsCancelling(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/inventaires.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action: 'annuler', id: id })
            });

            const result = await response.json();

            if (result.success) {
                setShowCancelModal(false);
                showToast('warning', 'Inventaire annulé', 'L\'inventaire a été annulé');
                fetchInventaire();
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de l'annulation");
            }
        } catch (err) {
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setIsCancelling(false);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', icon: 'ri-time-line',        label: 'Brouillon' },
            valide:    { gradient: 'linear-gradient(135deg, #10b981, #059669)', icon: 'ri-check-double-line', label: 'Validé' },
            annule:    { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', icon: 'ri-close-circle-line', label: 'Annulé' }
        };
        const config = configs[statut] || configs.brouillon;
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    const calculerTauxPrecision = () => {
        if (!inventaire?.lignes) return 0;
        const produitsSansEcart = inventaire.lignes.filter(l => l.ecart === 0).length;
        return Math.round((produitsSansEcart / inventaire.lignes.length) * 100) || 0;
    };

    /* ============================================================
       LOADING — Bootstrap spinner (conservé)
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <Spinner
                                animation="border"
                                role="status"
                                className="mb-3"
                                style={{ width: '3rem', height: '3rem', color: COLORS.primary }}
                            >
                                <span className="visually-hidden">Chargement...</span>
                            </Spinner>
                            <h5 className="text-muted">Chargement de l'inventaire...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !inventaire) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="empty-state-charte">
                        <div className="empty-state-icon-charte" style={{ background: 'rgba(239,68,68,0.1)', color: COLORS.danger }}>
                            <i className="ri-error-warning-line"></i>
                        </div>
                        <h6 className="fw-semibold mb-1">Erreur de chargement</h6>
                        <p className="text-muted small mb-3">{error || "Inventaire non trouvé"}</p>
                        <Button className="btn-modern-outline" onClick={() => navigate('/stock/inventaire')}>
                            <i className="ri-arrow-left-line me-1"></i>Retour
                        </Button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const tauxPrecision = calculerTauxPrecision();

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-clipboard-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">{inventaire.reference_inventaire}</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-calendar-line"></i> {formatDate(inventaire.date_inventaire)}
                                    </span>
                                    <span className="badge-modern">
                                        <i className="ri-user-line"></i> {inventaire.prenom_utilisateur} {inventaire.nom_utilisateur}
                                    </span>
                                    {getStatutBadge(inventaire.statut)}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => navigate('/stock/inventaire')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                            {inventaire.statut === 'brouillon' && (
                                <>
                                    <Button className="btn-modern-success" onClick={() => setShowValidateModal(true)}>
                                        <i className="ri-check-double-line"></i> Valider
                                    </Button>
                                    <Button className="btn-modern-danger" onClick={() => setShowCancelModal(true)}>
                                        <i className="ri-close-circle-line"></i> Annuler
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-stack-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Produits comptés</span>
                                    <h3 className="kpi-value-charte">{inventaire.lignes?.length || 0}</h3>
                                    <span className="kpi-unit-charte">articles</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className={`kpi-charte ${inventaire.total_ecart > 0 ? 'kpi-success' : inventaire.total_ecart < 0 ? 'kpi-danger' : 'kpi-info'}`}>
                                <div className="kpi-icon-charte">
                                    <i className={`ri-${inventaire.total_ecart > 0 ? 'arrow-up' : inventaire.total_ecart < 0 ? 'arrow-down' : 'equal'}-line`}></i>
                                </div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Écart total</span>
                                    <h3 className="kpi-value-charte">
                                        {inventaire.total_ecart > 0 ? '+' : ''}{inventaire.total_ecart || 0}
                                    </h3>
                                    <span className="kpi-unit-charte">unités</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte"><i className="ri-money-euro-circle-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Valeur écart</span>
                                    <h3 className="kpi-value-charte" style={{ fontSize: '1rem' }}>
                                        {formatCurrency(inventaire.valeur_ecart || 0)}
                                    </h3>
                                    <span className="kpi-unit-charte">FCFA</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-percent-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Précision</span>
                                    <h3 className="kpi-value-charte">{tauxPrecision}%</h3>
                                    <div className="progress-bar-charte mt-1">
                                        <div className="progress-fill-charte" style={{ width: `${tauxPrecision}%`, background: COLORS.info }}></div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== COMMENTAIRE ===== */}
                    {inventaire.commentaire && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Body className="p-4">
                                <div className="section-title-charte">
                                    <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                        <i className="ri-chat-1-line"></i>
                                    </span>
                                    <h6>Commentaire</h6>
                                </div>
                                <p className="mb-0 text-muted">{inventaire.commentaire}</p>
                            </Card.Body>
                        </Card>
                    )}

                    {/* ===== DÉTAIL DES LIGNES ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-list-check-2 text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Détail de l'inventaire</span>
                                    <span className="badge-header-light">{inventaire.lignes?.length || 0} produits</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Référence</th>
                                            <th>Produit</th>
                                            <th>Catégorie</th>
                                            <th className="text-center">Théorique</th>
                                            <th className="text-center">Réel</th>
                                            <th className="text-center">Écart</th>
                                            <th className="text-end">Valeur écart</th>
                                            <th className="pe-4">Commentaire</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inventaire.lignes?.map((ligne) => {
                                            const ecart = ligne.ecart || 0;
                                            const prix = ligne.prix_achat_ht || 0;
                                            const valeurEcart = ecart * prix;
                                            const rowClass = ecart > 0 ? 'row-positive-charte' : ecart < 0 ? 'row-negative-charte' : '';
                                            return (
                                                <tr key={ligne.id_ligne_inventaire} className={rowClass}>
                                                    <td className="ps-4">
                                                        <span className="reference-badge-charte">{ligne.reference}</span>
                                                    </td>
                                                    <td>
                                                        <Link to={`/stock/produits/${ligne.id_produit}`} className="table-link-charte">
                                                            {ligne.designation}
                                                        </Link>
                                                    </td>
                                                    <td>
                                                        {ligne.nom_categorie ? (
                                                            <span className="category-badge-charte">{ligne.nom_categorie}</span>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="quantity-badge-charte">{ligne.quantite_theorique}</span>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="quantity-badge-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                            {ligne.quantite_reelle}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        {ecart !== 0 ? (
                                                            <span className="status-badge-charte" style={{
                                                                background: ecart > 0
                                                                    ? 'linear-gradient(135deg, #10b981, #059669)'
                                                                    : 'linear-gradient(135deg, #ef4444, #dc2626)'
                                                            }}>
                                                                <i className={`ri-${ecart > 0 ? 'arrow-up' : 'arrow-down'}-line`}></i>
                                                                {ecart > 0 ? '+' : ''}{ecart}
                                                            </span>
                                                        ) : (
                                                            <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #64748b, #475569)' }}>
                                                                <i className="ri-equal-line"></i>0
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        <span className={`fw-semibold ${valeurEcart !== 0 ? '' : 'text-muted'}`}
                                                              style={{ color: valeurEcart !== 0 ? (valeurEcart > 0 ? COLORS.success : COLORS.danger) : undefined }}>
                                                            {formatCurrency(valeurEcart)}
                                                        </span>
                                                    </td>
                                                    <td className="pe-4">
                                                        {ligne.commentaire ? (
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>{ligne.commentaire}</Tooltip>}>
                                                                <span className="comment-preview-charte">
                                                                    <i className="ri-chat-1-line me-1"></i>
                                                                    {ligne.commentaire.length > 30 ? ligne.commentaire.substring(0, 30) + '...' : ligne.commentaire}
                                                                </span>
                                                            </OverlayTrigger>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                {/* ===== MODAL VALIDATION ===== */}
                <Modal show={showValidateModal} onHide={() => setShowValidateModal(false)} centered className="modal-charte">
                    <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.success}, #059669)` }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="modal-header-icon"><i className="ri-check-double-line"></i></div>
                            <div>
                                <Modal.Title className="fs-5 fw-bold text-white mb-0">Valider l'inventaire</Modal.Title>
                                <p className="text-white-50 mb-0 small">Les écarts seront appliqués au stock</p>
                            </div>
                        </div>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        <p className="text-center text-muted mb-4">
                            Êtes-vous sûr de vouloir valider l'inventaire <strong style={{ color: COLORS.primary }}>{inventaire?.reference_inventaire}</strong> ?
                        </p>

                        <div className="summary-card-charte mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-muted small">Produits avec écart</span>
                                <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.warning}, #d97706)` }}>
                                    {inventaire?.lignes?.filter(l => l.ecart !== 0).length}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-muted small">Écart total</span>
                                <span className="fw-bold" style={{
                                    color: inventaire?.total_ecart > 0 ? COLORS.success : inventaire?.total_ecart < 0 ? COLORS.danger : COLORS.info
                                }}>
                                    {inventaire?.total_ecart > 0 ? '+' : ''}{inventaire?.total_ecart || 0}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted small">Valeur totale des écarts</span>
                                <span className="fw-bold" style={{ color: COLORS.primary }}>
                                    {formatCurrency(inventaire?.valeur_ecart || 0)}
                                </span>
                            </div>
                        </div>

                        <div className="alert-error-charte">
                            <div className="d-flex align-items-center">
                                <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                                <div className="ms-3">
                                    <strong>Attention</strong>
                                    <p className="mb-0 small">Cette action est irréversible. Les écarts seront appliqués au stock en temps réel.</p>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                        <Button className="btn-modern-outline" onClick={() => setShowValidateModal(false)} disabled={isValidating}>
                            Annuler
                        </Button>
                        <Button className="btn-modern-success" onClick={handleValider} disabled={isValidating}>
                            {isValidating ? <><Spinner size="sm" className="me-2" />Validation...</> : <><i className="ri-check-double-line me-2"></i>Confirmer</>}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* ===== MODAL ANNULATION ===== */}
                <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered className="modal-charte">
                    <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="modal-header-icon"><i className="ri-close-circle-line"></i></div>
                            <div>
                                <Modal.Title className="fs-5 fw-bold text-white mb-0">Annuler l'inventaire</Modal.Title>
                                <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                            </div>
                        </div>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        <p className="text-center text-muted mb-3">
                            Êtes-vous sûr de vouloir annuler l'inventaire <strong style={{ color: COLORS.primary }}>{inventaire?.reference_inventaire}</strong> ?
                        </p>
                        <div className="summary-card-charte mb-3">
                            <div className="d-flex align-items-center gap-3">
                                <div className="categorie-avatar-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                                    <i className="ri-clipboard-line"></i>
                                </div>
                                <div>
                                    <div className="fw-bold" style={{ color: COLORS.primaryDark }}>
                                        {inventaire?.reference_inventaire}
                                    </div>
                                    <div className="text-muted small">
                                        <i className="ri-calendar-line me-1"></i>
                                        {formatDate(inventaire?.date_inventaire)}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="alert-error-charte mb-0">
                            <div className="d-flex align-items-center">
                                <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                                <div className="ms-3">
                                    <strong>Attention</strong>
                                    <p className="mb-0 small">L'annulation supprimera définitivement cet inventaire</p>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                        <Button className="btn-modern-outline" onClick={() => setShowCancelModal(false)} disabled={isCancelling}>
                            Non, conserver
                        </Button>
                        <Button className="btn-modern-danger" onClick={handleAnnuler} disabled={isCancelling}>
                            {isCancelling ? <><Spinner size="sm" className="me-2" />Annulation...</> : <><i className="ri-close-circle-line me-2"></i>Oui, annuler</>}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-charte tbody tr.row-positive-charte { background: #ecfdf5; }
                .table-charte tbody tr.row-positive-charte:hover { background: #d1fae5; }
                .table-charte tbody tr.row-negative-charte { background: #fef2f2; }
                .table-charte tbody tr.row-negative-charte:hover { background: #fee2e2; }

                .table-link-charte {
                    color: var(--primary);
                    text-decoration: none;
                    font-weight: 600;
                    font-size: 0.85rem;
                }
                .table-link-charte:hover { text-decoration: underline; }

                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .category-badge-charte {
                    display: inline-flex; align-items: center;
                    background: ${COLORS.primary}15;
                    color: var(--primary);
                    padding: 0.25rem 0.65rem;
                    border-radius: 50px;
                    font-weight: 600; font-size: 0.7rem;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }
                .quantity-badge-charte {
                    display: inline-flex; align-items: center; justify-content: center;
                    min-width: 45px; padding: 0.25rem 0.6rem;
                    background: #f1f5f9; border-radius: 12px;
                    font-weight: 600; font-size: 0.8rem; color: #475569;
                }

                /* ===== PROGRESS ===== */
                .progress-bar-charte {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 6px;
                    overflow: hidden;
                }
                .progress-fill-charte {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }

                /* ===== COMMENT ===== */
                .comment-preview-charte {
                    cursor: pointer;
                    display: inline-block;
                    max-width: 150px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    color: #475569;
                    font-size: 0.85rem;
                }
                .comment-preview-charte:hover { color: var(--primary); }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .summary-card-charte {
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    padding: 1rem 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .categorie-avatar-charte {
                    width: 52px; height: 52px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-weight: 700; font-size: 1.2rem;
                    flex-shrink: 0;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.25);
                }
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled,
                .btn-modern-success:disabled,
                .btn-modern-danger:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}
